import React, { useState } from 'react';

const VisualFamilyTree = ({ familyMembers, onEditMember, onDeleteMember }) => {
  const [hoveredMember, setHoveredMember] = useState(null);

  // Organize members by generation and relationship
  const organizeByGeneration = (members) => {
    const generations = {
      grandparents: [],
      parents: [],
      self: [],
      children: [],
      extended: []
    };

    members.forEach(member => {
      switch (member.relationship) {
        case 'Umkhulu':
        case 'Ugogo':
          generations.grandparents.push(member);
          break;
        case 'uBaba':
        case 'uMama':
          generations.parents.push(member);
          break;
        case 'uBhuti':
        case 'uSisi':
          generations.self.push(member);
          break;
        case 'iNdodana':
        case 'iNdodakazi':
          generations.children.push(member);
          break;
        case 'uMzala':
        case 'uMalume':
        default:
          generations.extended.push(member);
          break;
      }
    });

    return generations;
  };

  const generations = organizeByGeneration(familyMembers);

  const MemberNode = ({ member, size = 'normal' }) => {
    const memberId = member._id || member.id;
    const isHovered = hoveredMember === memberId;
    
    const getNodeSize = () => {
      switch (size) {
        case 'large': return { width: '80px', height: '80px', fontSize: '24px' };
        case 'small': return { width: '48px', height: '48px', fontSize: '16px' };
        default: return { width: '64px', height: '64px', fontSize: '20px' };
      }
    };

    const nodeSize = getNodeSize();
    const textSize = size === 'large' ? '14px' : size === 'small' ? '11px' : '12px';

    return (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        margin: '8px',
        position: 'relative',
        transition: 'transform 0.2s ease'
      }}
      onMouseEnter={() => setHoveredMember(memberId)}
      onMouseLeave={() => setHoveredMember(null)}
      >
        {/* Photo Circle */}
        <div style={{
          width: nodeSize.width,
          height: nodeSize.height,
          borderRadius: '50%',
          backgroundColor: '#fbbf24',
          border: '3px solid #92400e',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: nodeSize.fontSize,
          color: '#92400e',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          transform: isHovered ? 'scale(1.1)' : 'scale(1)',
          boxShadow: isHovered ? '0 8px 16px rgba(146, 64, 14, 0.3)' : '0 2px 4px rgba(0,0,0,0.1)',
          position: 'relative',
          overflow: 'hidden'
        }}
        onClick={() => onEditMember && onEditMember(member)}
        >
          {member.photo ? (
            <img 
              src={member.photo} 
              alt={member.fullName}
              style={{
                width: '100%',
                height: '100%',
                borderRadius: '50%',
                objectFit: 'cover'
              }}
            />
          ) : (
            '👤'
          )}
          
          {/* Hover overlay */}
          {isHovered && (
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(146, 64, 14, 0.8)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontSize: '12px',
              fontWeight: 'bold'
            }}>
              Click to Edit
            </div>
          )}
        </div>

        {/* Name and Info */}
        <div style={{
          marginTop: '8px',
          textAlign: 'center',
          maxWidth: size === 'large' ? '100px' : '80px'
        }}>
          <div style={{
            fontSize: textSize,
            fontWeight: 'bold',
            color: '#92400e',
            lineHeight: '1.2',
            marginBottom: '2px'
          }}>
            {member.fullName}
          </div>
          
          {member.isibongo && (
            <div style={{
              fontSize: size === 'small' ? '9px' : '10px',
              color: '#d97706',
              fontStyle: 'italic',
              marginBottom: '2px'
            }}>
              {member.isibongo}
            </div>
          )}

          <div style={{
            fontSize: size === 'small' ? '8px' : '9px',
            color: '#6b7280',
            backgroundColor: '#fef3c7',
            padding: '2px 6px',
            borderRadius: '8px',
            marginBottom: '2px'
          }}>
            {member.relationship}
          </div>

          {member.birthDate && (
            <div style={{
              fontSize: size === 'small' ? '8px' : '9px',
              color: '#9ca3af'
            }}>
              Born: {new Date(member.birthDate).getFullYear()}
            </div>
          )}
        </div>

        {/* Action buttons on hover */}
        {isHovered && (
          <div style={{
            position: 'absolute',
            bottom: '-45px',
            display: 'flex',
            gap: '4px',
            opacity: 1,
            transition: 'opacity 0.2s ease',
            zIndex: 10
          }}>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onEditMember && onEditMember(member);
              }}
              style={{
                backgroundColor: '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                padding: '4px 8px',
                fontSize: '10px',
                cursor: 'pointer',
                boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
              }}
            >
              Edit
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDeleteMember && onDeleteMember(memberId);
              }}
              style={{
                backgroundColor: '#ef4444',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                padding: '4px 8px',
                fontSize: '10px',
                cursor: 'pointer',
                boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
              }}
            >
              Delete
            </button>
          </div>
        )}
      </div>
    );
  };

  const TreeBranch = ({ direction = 'down', height = 30 }) => (
    <div style={{
      width: '3px',
      height: `${height}px`,
      backgroundColor: '#92400e',
      margin: '0 auto',
      position: 'relative'
    }}>
      {direction === 'down' && (
        <div style={{
          position: 'absolute',
          bottom: 0,
          left: '50%',
          transform: 'translateX(-50%)',
          width: 0,
          height: 0,
          borderLeft: '5px solid transparent',
          borderRight: '5px solid transparent',
          borderTop: '10px solid #92400e'
        }}></div>
      )}
    </div>
  );

  const HorizontalBranch = ({ width = 100 }) => (
    <div style={{
      width: `${width}px`,
      height: '3px',
      backgroundColor: '#92400e',
      margin: '15px auto',
      position: 'relative'
    }}>
      {/* Decorative leaves */}
      <div style={{
        position: 'absolute',
        top: '-8px',
        left: '20%',
        color: '#16a34a',
        fontSize: '16px'
      }}>🌿</div>
      <div style={{
        position: 'absolute',
        top: '-8px',
        right: '20%',
        color: '#16a34a',
        fontSize: '16px'
      }}>🌿</div>
    </div>
  );

  if (familyMembers.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '48px' }}>
        <div style={{ fontSize: '72px', marginBottom: '16px' }}>🌳</div>
        <h2 style={{ fontSize: '24px', fontWeight: '600', color: '#6b7280', marginBottom: '8px' }}>
          Umthsawu wakho ukhona (Your Tree Awaits)
        </h2>
        <p style={{ color: '#9ca3af' }}>
          Start building your family tree by adding your first family member
        </p>
      </div>
    );
  }

  return (
    <div style={{ 
      maxWidth: '1200px', 
      margin: '0 auto', 
      padding: '16px',
      overflowX: 'auto'
    }}>
      {/* Tree Header */}
      <div style={{ textAlign: 'center', marginBottom: '32px' }}>
        <h2 style={{ 
          fontSize: '28px', 
          fontWeight: 'bold', 
          color: '#92400e', 
          marginBottom: '8px' 
        }}>
          🌳 Umthsawu Womndeni (Family Tree)
        </h2>
        <p style={{ color: '#6b7280' }}>
          {familyMembers.length} family member{familyMembers.length !== 1 ? 's' : ''} recorded
        </p>
      </div>

      {/* Visual Tree Structure */}
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center',
        minWidth: '800px',
        background: 'linear-gradient(to bottom, #fef3c7 0%, #fbbf24 1%, #fef3c7 2%, #fef3c7 100%)',
        borderRadius: '20px',
        padding: '40px 20px',
        position: 'relative'
      }}>
        
        {/* Tree canopy decoration */}
        <div style={{
          position: 'absolute',
          top: '10px',
          left: '50%',
          transform: 'translateX(-50%)',
          color: '#16a34a',
          fontSize: '32px',
          opacity: 0.3
        }}>
          🌿🌿🌿
        </div>
        
        {/* Grandparents Generation */}
        {generations.grandparents.length > 0 && (
          <>
            <div style={{
              fontSize: '14px',
              color: '#92400e',
              fontWeight: 'bold',
              marginBottom: '16px',
              textAlign: 'center'
            }}>
              Grandparents (Omkhulu / Ogogo)
            </div>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              gap: '40px',
              marginBottom: '16px'
            }}>
              {generations.grandparents.map(member => (
                <MemberNode key={member._id || member.id} member={member} size="small" />
              ))}
            </div>
            <TreeBranch height={40} />
          </>
        )}

        {/* Parents Generation */}
        {generations.parents.length > 0 && (
          <>
            <div style={{
              fontSize: '14px',
              color: '#92400e',
              fontWeight: 'bold',
              marginBottom: '16px',
              textAlign: 'center'
            }}>
              Parents (Abazali)
            </div>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              gap: '60px',
              marginBottom: '16px'
            }}>
              {generations.parents.map(member => (
                <MemberNode key={member._id || member.id} member={member} size="normal" />
              ))}
            </div>
            
            {generations.parents.length === 2 && (
              <HorizontalBranch width={140} />
            )}
            
            {(generations.self.length > 0 || generations.children.length > 0) && (
              <TreeBranch height={40} />
            )}
          </>
        )}

        {/* Current Generation (Siblings) */}
        {generations.self.length > 0 && (
          <>
            <div style={{
              fontSize: '14px',
              color: '#92400e',
              fontWeight: 'bold',
              marginBottom: '16px',
              textAlign: 'center'
            }}>
              Current Generation (Isizukulwane)
            </div>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              gap: '40px',
              marginBottom: '16px'
            }}>
              {generations.self.map(member => (
                <MemberNode key={member._id || member.id} member={member} size="large" />
              ))}
            </div>
            
            {generations.self.length > 1 && (
              <HorizontalBranch width={generations.self.length * 80} />
            )}
            
            {generations.children.length > 0 && <TreeBranch height={40} />}
          </>
        )}

        {/* Children Generation */}
        {generations.children.length > 0 && (
          <>
            <div style={{
              fontSize: '14px',
              color: '#92400e',
              fontWeight: 'bold',
              marginBottom: '16px',
              textAlign: 'center'
            }}>
              Children (Abantwana)
            </div>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              gap: '30px',
              marginBottom: '16px'
            }}>
              {generations.children.map(member => (
                <MemberNode key={member._id || member.id} member={member} size="normal" />
              ))}
            </div>
          </>
        )}

        {/* Extended Family (Side branch) */}
        {generations.extended.length > 0 && (
          <>
            <div style={{ 
              marginTop: '40px',
              textAlign: 'center',
              borderTop: '2px dashed #92400e',
              paddingTop: '20px',
              width: '100%'
            }}>
              <h3 style={{ 
                fontSize: '16px', 
                color: '#92400e', 
                marginBottom: '16px',
                fontWeight: 'bold'
              }}>
                Extended Family (Umndeni Ophelelayo)
              </h3>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                gap: '30px',
                flexWrap: 'wrap'
              }}>
                {generations.extended.map(member => (
                  <MemberNode key={member._id || member.id} member={member} size="small" />
                ))}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Tree Root Decoration */}
      <div style={{ 
        textAlign: 'center', 
        marginTop: '40px',
        color: '#92400e',
        backgroundColor: 'white',
        padding: '20px',
        borderRadius: '12px',
        boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
      }}>
        <div style={{ fontSize: '32px', marginBottom: '8px' }}>🌿</div>
        <p style={{ fontSize: '14px', fontStyle: 'italic', marginBottom: '4px' }}>
          "Ukuze uzazi ukuthi uyaphi, kufanele wazi ukuthi uvela kuphi"
        </p>
        <p style={{ fontSize: '12px', color: '#6b7280' }}>
          "To know where you are going, you must know where you come from"
        </p>
      </div>

      {/* Instructions */}
      <div style={{
        marginTop: '20px',
        padding: '16px',
        backgroundColor: '#fef3c7',
        borderRadius: '8px',
        textAlign: 'center'
      }}>
        <p style={{ fontSize: '14px', color: '#92400e' }}>
          💡 <strong>How to use:</strong> Hover over family members to see edit/delete options. Click on photos to edit member details.
        </p>
      </div>
    </div>
  );
};

export default VisualFamilyTree;